import React, { useState, useEffect, useCallback } from "react";
import MainLayout from "../Themes/MainLayout";
import { getConfirmDeleteDialog } from "../Components/confirmDeleteDialog";
import axios from "axios";
import Table_theme from "../Themes/TableTheme";
import {
  Alert,
  CssBaseline,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
  Popover,
  MenuItem,
  InputAdornment,
  Switch,
  Pagination,
  Typography,
  ThemeProvider,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FlagIcon from "@mui/icons-material/Flag";
import { Add, FilterList, Search, SportsMartialArts, Close } from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import FormThemes from "../Themes/FormThemes";
import moment from "moment";
import { useCurrentUser } from "../utils/useCurrentUser";
import { formatDateLocal_TF } from "../utils/dateUtils.jsx";

// ---------------------------------------------------------------------------
// IST helpers — all date operations in this file use IST (Asia/Kolkata, +05:30)
// regardless of the browser's local timezone.
// ---------------------------------------------------------------------------
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // +05:30 in ms

/**
 * Parse a "YYYY-MM-DD" string as IST midnight and return a moment object
 * that the MUI DatePicker can display correctly (without UTC off-by-one).
 */
const parseISTDate = (dateStr) => {
  if (!dateStr) return null;
  const d = String(dateStr).slice(0, 10); // trim any time component
  return moment(d, "YYYY-MM-DD"); // local parse — keeps the calendar date intact
};

/**
 * Format a moment/Date value chosen in the DatePicker to "YYYY-MM-DD" in IST.
 * The picker gives us a moment in local time; we re-interpret it as IST.
 */
const formatISTDate = (momentValue) => {
  if (!momentValue) return null;
  // moment(newValue).format gives us the local calendar date the user clicked —
  // since we want IST calendar date (not UTC), we just format as-is.
  return moment(momentValue).format("YYYY-MM-DD");
};

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const apiBaseUrl = `${BASE_URL}/api/leave-request`;

const leaveFields = [
  "employee_name",
  "leave_type",
  "leave_description",
  "approval_level_required",
  "approver_id",
  "duration_type",
  "start_date",
  "end_date",
  "total_days",
  "start_time",
  "end_time",
  "total_hours",
  "status",
  "approver_remarks",
  "submitted_at",
  "last_updated",
  "emergency_contact",
 
];

const popupFields = [
  "employee_id",
  "leave_type",
  "leave_description",
  "approval_level_required",
  "start_date",
  "end_date",
  "total_days",
  "start_time",
  "end_time",
  "total_hours",
  "emergency_contact",
];
const filterFieldMap = {
  employee_name: "employee_id",
  leave_type: "leave_type",
  status: "status",
  approver_id: "approver_name",
  total_days: "total_days",
  total_hours: "total_hours",
  start_date: "start_date",
  end_date: "end_date",
  start_time: "start_time",
  end_time: "end_time",
  approval_level_required: "approval_level_required",
  duration_type: "duration_type",
};
const fieldLabels = {};
leaveFields.forEach((field) => {
  fieldLabels[field] = field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
});
fieldLabels.bo_id = "Organization Id";
fieldLabels.employee_id = "Employee Name";
fieldLabels.employee_name = "Employee Name";
fieldLabels.leave_type = "Leave Type";
fieldLabels.approver_id = "Approver Name";

const LeaveRequest = ({ employeeId }) => {
  const { empId, boId, roles } = useCurrentUser();
  const [requests, setRequests] = useState([]);
  const [filters, setFilters] = useState({});
  const [errors, setErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  // cancel leave state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [projectResources, setProjectResources] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState({});
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(
    leaveFields.reduce((acc, f) => ({ ...acc, [f]: true }), {})
  );
  const [anchorEls, setAnchorEls] = useState({});
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [approvalLevel, setApprovalLevel] = useState(null);
  const [dialogError, setDialogError] = useState("");

  const [newRequest, setNewRequest] = useState({
    duration_type: "Full/Multiple Days",
    bo_id: boId,
    employee_id: empId || "",
    leave_type: "",
    leave_description: "",
    approval_level_required: null,
    start_date: null,
    end_date: null,
    total_days: null,
    start_time: null,
    end_time: null,
    total_hours: null,
    is_half_day: false,
    half_day_period: null,
    status: "Pending",
    emergency_contact: null,
    is_draft: false,
    alternative_resource: null,
  });
  const [editRequest, setEditRequest] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [longLeaveWarning, setLongLeaveWarning] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [search, setSearch] = useState("");
  const [boOptions, setBoOptions] = useState([]);
  const [boToEmployeesMap, setBoToEmployeesMap] = useState({});
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);
  const [leaveTypeLoading, setLeaveTypeLoading] = useState(false);
  // Org holidays cache: { "YYYY": [{ holiday_date: "YYYY-MM-DD", holiday_type }] }
  const [holidaysCache, setHolidaysCache] = useState({});

  const fetchHolidaysForYear = useCallback(async (year) => {
    if (!boId || holidaysCache[year]) return;
    try {
      const res = await fetch(`${BASE_URL}/api/holidays?bo_id=${boId}&year=${year}`);
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.data || [];
      setHolidaysCache(prev => ({ ...prev, [year]: list }));
    } catch (err) {
      console.error("Failed to fetch holidays for leave calc:", err);
    }
  }, [boId, holidaysCache]);

  const rowsPerPage = 10;

  const calculateTotalDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    const diffTime = end - start;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    return Math.round(diffDays + 1);
    //return diffDays + 1;
  };

  // Holiday-aware version: excludes weekends (already blocked in picker) and public holidays
  const calculateWorkingDays = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate + "T00:00:00");
    const end   = new Date(endDate   + "T00:00:00");

    // Collect all holiday dates across relevant years as a Set for O(1) lookup
    const holidaySet = new Set();
    const startYear = start.getFullYear();
    const endYear   = end.getFullYear();
    for (let y = startYear; y <= endYear; y++) {
      const list = holidaysCache[y] || [];
      list.forEach(h => {
        if (h.holiday_date) {
          const raw = String(h.holiday_date);
          const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(raw)
            ? raw
            : `${new Date(raw).getFullYear()}-${String(new Date(raw).getMonth()+1).padStart(2,"0")}-${String(new Date(raw).getDate()).padStart(2,"0")}`;
          // Full Day holiday counts as a full skip; Half Day still counts as a working day
          if ((h.holiday_type || "Full Day") === "Full Day") {
            holidaySet.add(dateStr);
          }
        }
      });
    }

    let count = 0;
    const cur = new Date(start);
    while (cur <= end) {
      const day = cur.getDay();
      const dateStr = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}-${String(cur.getDate()).padStart(2,"0")}`;
      // Skip weekends and full-day public holidays
      if (day !== 0 && day !== 6 && !holidaySet.has(dateStr)) {
        count++;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  }, [holidaysCache]);
  const getAlternativeResourceName = (empId) => {
    if (!empId) return "";
    const emp = projectResources.find((res) => res.employee_id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : empId;
  };
  const getEmployeeName = (row) => {
    const employees = boToEmployeesMap[row.bo_id] || [];
    const emp = employees.find((e) => e.employee_id === row.employee_id);
    return emp ? `${emp.first_name} ${emp.last_name}` : row.employee_id;
  };
  const calculateTotalHours = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    // --- Parse "HH:MM" + optional "AM/PM"
    const parseTime = (timeStr) => {
      const parts = timeStr.trim().split(" ");
      const [timePart, modifier] = parts;
      let [hours, minutes] = timePart.split(":").map(Number);
      // Convert 12-hour times to 24-hour
      if (modifier) {
        const mod = modifier.toUpperCase();
        if (mod === "PM" && hours < 12) hours += 12;
        if (mod === "AM" && hours === 12) hours = 0;
      }
      return { hours, minutes };
    };
    const { hours: startHours, minutes: startMinutes } = parseTime(startTime);
    const { hours: endHours, minutes: endMinutes } = parseTime(endTime);

    // --- Create Date objects on the same day
    const start = new Date(0, 0, 0, startHours, startMinutes);
    const end = new Date(0, 0, 0, endHours, endMinutes);

    // --- Handle overnight case (e.g., 10 PM → 6 AM)
    if (end <= start) end.setDate(end.getDate() + 1);

    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);

    return Number(diffHours.toFixed(2));
  };

  const confirmDelete = (request) => {
    if (!request || !request.request_id) {
      console.error("Invalid request object for delete");
      return;
    }
    setSelectedRequest(request);
    setDeleteDialogOpen(true);
  };
  const formatDays = (days) => {
    if (days === null || days === undefined) return "";
    const totalDays = Math.round(days); // round to nearest integer
    return totalDays === 1 ? "1" : `${totalDays} `;
  };
  const formatHours = (hours) => {
    if (!hours && hours !== 0) return "";

    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);

    if (minutes > 0) {
      return `${wholeHours} hour${
        wholeHours !== 1 ? "s" : ""
      } ${minutes} minute${minutes !== 1 ? "s" : ""}`;
    } else {
      return `${wholeHours} hour${wholeHours !== 1 ? "s" : ""}`;
    }
  };

  const validateRequest = (request, isEdit = false) => {
    const errors = {};
    // duration_type is now derived from leave_type — not a user-facing field
    const isPermission = (request.leave_type || "").trim().toLowerCase() === "permission"
      || (request.duration_type || "").toLowerCase() === "specific hours";
    const isHalfDay = (request.duration_type || "").toLowerCase() === "half day"
      || (request.leave_type || "").trim().toLowerCase() === "half day leave";
    const requiredFields = [
      "bo_id",
      "employee_id",
      "leave_type",
      "leave_description",
      "approval_level_required",
      "start_date",
    ];

    // Approval level validation
    if (
      request.approval_level_required &&
      (request.approval_level_required < 1 ||
        request.approval_level_required > 3)
    ) {
      errors.approval_level_required = "Approval level must be 1, 2, or 3 only";
    }

    if (isHalfDay) {
      // Half day: only start_date + half_day_period required, no end_date
      if (!request.half_day_period) {
        errors.half_day_period = "Please select First Half or Second Half";
      }
    } else if (!isPermission) {
      requiredFields.push("end_date");
      if (request.end_date) requiredFields.push("total_days");
    } else if ((request.duration_type || "").toLowerCase() === "half day") {
      if (!request.half_day_period) {
        errors.half_day_period = "Please select First Half or Second Half";
      }
    } else {
      requiredFields.push("start_time", "end_time", "total_hours");
    }

    // Required field check
    requiredFields.forEach((field) => {
      if (!request[field] && request[field] !== 0 && request[field] !== false) {
        errors[field] = `${fieldLabels[field] || field} is required`;
      }
    });

    // Leave description cannot be only whitespace
    if (request.leave_description && !request.leave_description.trim()) {
      errors.leave_description = "Leave Description cannot be empty or spaces only";
    }

    // Optional field validation
    if (request.emergency_contact) {
      const mobileRegex = /^\+?[0-9]{10,15}$/; // supports optional country code
      if (!mobileRegex.test(request.emergency_contact.trim())) {
        errors.emergency_contact = "Please enter a valid mobile number";
      }
    }

    // --- DATE VALIDATION ---
    if (request.start_date) {
      const today = moment().startOf("day");
      const start = moment(request.start_date);

      // Only check for past date when adding (not editing)
      const earliest = moment().subtract(30, "days").startOf("day");
      if (!isEdit && start.isBefore(earliest)) {
        errors.start_date = "Start date cannot be more than 30 days in the past";
      }
    }

    if (request.start_date && request.end_date) {
      const start = moment(request.start_date);
      const end = moment(request.end_date);
      if (end.isBefore(start)) {
        errors.end_date = "End date must be on or after start date";
      }
    }
   // --- TIME VALIDATION ---
    if (isPermission) {
      if (request.start_time && request.end_time) {
        const [sh, sm] = request.start_time.split(":").map(Number);
        const [eh, em] = request.end_time.split(":").map(Number);
        let start = new Date(0, 0, 0, sh, sm);
        let end = new Date(0, 0, 0, eh, em);

        if (end <= start) {
          end.setDate(end.getDate() + 1);
        }

        const diff = (end - start) / (1000 * 60 * 60);
        if (diff <= 0) {
          errors.total_hours = "Total hours must be greater than 0";
        }
        if (diff > 24) {
          errors.total_hours = "Total hours cannot exceed 24 hours";
        }
      }
    }

    return errors;
  };

  const handleFilterIconClick = (field, event) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleFilterClose = (field) => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };
  const fetchProjectResourcesForEmployee = useCallback(
    async (targetEmployeeId) => {
      try {
        if (!targetEmployeeId) {
          setProjectResources([]);
          return;
        }
        setResourcesLoading(true);
        const res = await axios.get(
          `${apiBaseUrl}/project-resources/${targetEmployeeId}`,
          { params: { bo_id: boId } }
        );
        setProjectResources(res.data || []);
      } catch (err) {
        console.error("Error fetching project resources:", err);
        setProjectResources([]);
      } finally {
        setResourcesLoading(false);
      }
    },
    [boId]
  );
  const fetchData = useCallback(async () => {
    try {
      const mappedFilters = {};
      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          if (
            ["start_date", "end_date", "submitted_at", "last_updated"].includes(
              key,
            )
          ) {
            const parsedDate = moment(filters[key], "MM/DD/YYYY", true);
            if (parsedDate.isValid()) {
              mappedFilters[filterFieldMap[key] || key] =
                parsedDate.format("YYYY-MM-DD");
            } else {
              mappedFilters[filterFieldMap[key] || key] = filters[key];
            }
          } else {
            mappedFilters[filterFieldMap[key] || key] = filters[key];
          }
        }
      });

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...mappedFilters,
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(statusFilter ? { status: statusFilter } : { status: "All" }),
        ...(boId ? { bo_id: boId } : {}),
        ...(empId ? { employee_id: empId } : {}),
      };

      const response = await axios.get(`${apiBaseUrl}/paginated`, { params });
      const enrichedRequests = await Promise.all(
        response.data.data.map(async (row) => {
          const history = await fetchApprovalHistory(row.request_id);
          return { ...row, approvalHistory: history };
        }),
      );

      setRequests(enrichedRequests);
      setTotalCount(response.data.total);
    } catch (err) {
      console.error("Error fetching paginated leave requests:", err);
      setSnackbarMsg("Failed to fetch requests");
      setShowSnackbar(true);
    }
  }, [page, filters, search, statusFilter, empId, boId]);

  useEffect(() => {
    if (alertMsg) {
      const timer = setTimeout(() => {
        setAlertMsg("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alertMsg]);

  useEffect(() => {
    const fetchStatusFlags = async () => {
      try {
        const res = await fetch(`${BASE_URL}/commonVariables/statusFlag`);
        const data = await res.json();
        setStatusOptions(data);
      } catch (err) {
        console.error("Failed to fetch employee status flags:", err);
      }
    };

    fetchStatusFlags();
  }, []);
  useEffect(() => {
    if (!newRequest.employee_id) return;

    axios
      .get(
        `${BASE_URL}/api/leave-request/approval-level/${newRequest.employee_id}`,
        { params: { bo_id: boId } },
      )
      .then((res) => {
        const level = res.data.approval_level_required || "";
        setApprovalLevel(level); // store it globally
        setNewRequest((prev) => ({
          ...prev,
          approval_level_required: level,
        }));
      })
      .catch((err) => {
        console.error("Error fetching approval level:", err);
      });
  }, [newRequest.employee_id, boId]);

  useEffect(() => {
    if (!editRequest.employee_id) return;

    axios
      .get(
        `${BASE_URL}/api/leave-request/approval-level/${editRequest.employee_id}`,
        { params: { bo_id: boId } },
      )
      .then((res) => {
        const level = res.data.approval_level_required || "";
        setEditRequest((prev) => ({
          ...prev,
          approval_level_required: level,
        }));
      })
      .catch((err) => {
        console.error("Error fetching approval level for edit:", err);
      });
  }, [editRequest.employee_id, boId]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useEffect(() => {
    if (newRequest.employee_id) {
      fetchProjectResourcesForEmployee(newRequest.employee_id);
    }
  }, [newRequest.employee_id, boId, fetchProjectResourcesForEmployee]);

  // Auto-calculate start_time/end_time from shift when half_day_period changes (new request)
  useEffect(() => {
    if (!newRequest.is_half_day || !newRequest.half_day_period || !newRequest.employee_id) return;
    const empId = newRequest.employee_id;
    const reqBoId = newRequest.bo_id || boId;
    fetch(`${BASE_URL}/commonVariables/shift-timings/${reqBoId}/${empId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const times = newRequest.half_day_period === "First Half" ? data.first_half : data.second_half;
        const totalHours = calculateTotalHours(times.start, times.end);
        setNewRequest(prev => ({
          ...prev,
          start_time: times.start,
          end_time: times.end,
          total_hours: totalHours,
        }));
      })
      .catch(console.error);
  }, [newRequest.half_day_period, newRequest.employee_id, newRequest.is_half_day]);
  useEffect(() => {
    if (editRequest.employee_id) {
      fetchProjectResourcesForEmployee(editRequest.employee_id);
    }
  }, [editRequest.employee_id, boId, fetchProjectResourcesForEmployee]);

  // Auto-calculate start_time/end_time from shift when half_day_period changes (edit request)
  useEffect(() => {
    if (!editRequest.is_half_day || !editRequest.half_day_period || !editRequest.employee_id) return;
    const empId = editRequest.employee_id;
    const reqBoId = editRequest.bo_id || boId;
    fetch(`${BASE_URL}/commonVariables/shift-timings/${reqBoId}/${empId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const times = editRequest.half_day_period === "First Half" ? data.first_half : data.second_half;
        const totalHours = calculateTotalHours(times.start, times.end);
        setEditRequest(prev => ({
          ...prev,
          start_time: times.start,
          end_time: times.end,
          total_hours: totalHours,
        }));
      })
      .catch(console.error);
  }, [editRequest.half_day_period, editRequest.employee_id, editRequest.is_half_day]);
  useEffect(() => {
    setPage(0);
  }, [search, filters, statusFilter]);

  // Pre-fetch holidays for current year (and adjacent years) on mount
  useEffect(() => {
    if (!boId) return;
    const currentYear = new Date().getFullYear();
    fetchHolidaysForYear(currentYear);
    fetchHolidaysForYear(currentYear + 1);
  }, [boId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const normalizeRoles = (roles) => {
      if (!roles) return [];
      return Array.isArray(roles)
        ? roles.map((r) => r.toLowerCase())
        : [roles.toLowerCase()];
    };

    const normalizedRoles = normalizeRoles(roles);
    const isAdmin = normalizedRoles.includes("admin");

    const params = {};
    if (!isAdmin) {
      if (boId) params.bo_id = boId;
      if (empId) params.employee_id = empId;
    }

    axios
      .get(`${apiBaseUrl}/bo-dropdown-options`, { params })
      .then((res) => {
        const boSet = new Set();
        const map = {};
        res.data.forEach((row) => {
          if (row.bo_id) {
            boSet.add(row.bo_id);
            if (!map[row.bo_id]) {
              map[row.bo_id] = [];
            }
            map[row.bo_id].push({
              employee_id: row.employee_id,
              first_name: row.first_name,
              last_name: row.last_name,
            });
          }
        });
        setBoOptions([...boSet]);
        setBoToEmployeesMap(map);
      })
      .catch((err) => {
        console.error("Error fetching BO options:", err);
        setSnackbarMsg("Failed to fetch organization options");
        setShowSnackbar(true);
      });

    setLeaveTypeLoading(true);
    axios
      .get(`${apiBaseUrl}/leave-types`)
      .then((res) => {
        const leaveTypes = res.data.client_type || [];
        if (!Array.isArray(leaveTypes) || !leaveTypes.length) {
          setSnackbarMsg(
            "No leave types available. Please contact the administrator.",
          );
          setShowSnackbar(true);
        }
        setLeaveTypeOptions(leaveTypes);
      })
      .catch((err) => {
        console.error("Error fetching leave types:", err);
        setSnackbarMsg(
          err.response?.data?.error || "Failed to fetch leave types",
        );
        setShowSnackbar(true);
      })
      .finally(() => {
        setLeaveTypeLoading(false);
      });
  }, []);

  const handleAdd = async () => {
    if (isSaving) return;
    const errors = validateRequest(newRequest);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      setAlertSeverity("error");
      return;
    }
    // Warn if more than 3 days
    if (newRequest.total_days && newRequest.total_days > 3) {
      setPendingRequest(newRequest);
      setLongLeaveWarning(true);
      return;
    }
    await submitLeaveRequest(newRequest);
  };

  const submitLeaveRequest = async (request) => {
    setIsSaving(true);
    const transformedRequest = {
      ...request,
      created_by: empId,
      leave_description: (request.leave_description || "").trim(),
      start_date: request.start_date
        ? moment(String(request.start_date).slice(0, 10), "YYYY-MM-DD").format("YYYY-MM-DD")
        : null,
      end_date: request.end_date
        ? moment(String(request.end_date).slice(0, 10), "YYYY-MM-DD").format("YYYY-MM-DD")
        : null,
      total_days: request.total_days || null,
      start_time: request.start_time ? `${request.start_time}:00` : null,
      end_time: request.end_time ? `${request.end_time}:00` : null,
      total_hours: request.total_hours || null,
      emergency_contact: request.emergency_contact || null,
      alternative_resource: request.alternative_resource || null,
      is_draft: request.is_draft || false,
      is_half_day: request.is_half_day || false,
      half_day_period: request.is_half_day ? (request.half_day_period || null) : null,
    };

    try {
      await axios.post(apiBaseUrl, transformedRequest, {
        params: { bo_id: boId },
      });
      setOpen(false);

      const normalizeRoles = (roles) => {
        if (!roles) return [];
        return Array.isArray(roles)
          ? roles.map((r) => r.toLowerCase())
          : [roles.toLowerCase()];
      };

      const normalizedRoles = normalizeRoles(roles);
      const isAdmin = normalizedRoles.includes("admin");

      setNewRequest({
        duration_type: "Full/Multiple Days",
        bo_id: !isAdmin ? boId || "" : "",
        employee_id: !isAdmin ? employeeId || empId || "" : "",
        leave_type: "",
        leave_description: "",
        approval_level_required: null,
        approver_id: "",
        start_date: null,
        end_date: null,
        total_days: null,
        start_time: null,
        end_time: null,
        total_hours: null,
        is_half_day: false,
        half_day_period: null,
        status: null,
        emergency_contact: null,
        is_draft: false,
        alternative_resource: null,
      });
      setErrors({});
      setAlertMsg("Request added successfully!");
      setAlertSeverity("success");
      fetchData();
    } catch (err) {
      console.error("Add failed:", err.response?.data || err.message);
      setDialogError(err.response?.data?.error || "Failed to add request!");
      setAlertSeverity("error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async () => {
    const errors = validateRequest(editRequest, true);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);

      setAlertMsg("Please fill all required fields correctly!");
      setAlertSeverity("error");
      return;
    }
    const transformedRequest = {
      bo_id: editRequest.bo_id,
      employee_id: editRequest.employee_id,
      leave_type: editRequest.leave_type,
      leave_description: editRequest.leave_description,
      approval_level_required: editRequest.approval_level_required || null,
      duration_type: editRequest.duration_type,
      start_date: editRequest.start_date
        ? moment(String(editRequest.start_date).slice(0, 10), "YYYY-MM-DD").format("YYYY-MM-DD")
        : null,
      end_date: editRequest.end_date
        ? moment(String(editRequest.end_date).slice(0, 10), "YYYY-MM-DD").format("YYYY-MM-DD")
        : null,
      total_days: editRequest.total_days,
      start_time: editRequest.start_time ? `${editRequest.start_time}:00` : null,
      end_time: editRequest.end_time ? `${editRequest.end_time}:00` : null,
      total_hours: editRequest.total_hours,
      status: editRequest.status || "Pending",
      emergency_contact: editRequest.emergency_contact || null,
      alternative_resource: editRequest.alternative_resource,
      is_draft: editRequest.is_draft || false,
      is_half_day: editRequest.is_half_day || false,
      half_day_period: editRequest.half_day_period || null,
      modified_by: empId,
    };

    try {
      await axios.put(
        `${apiBaseUrl}/${editRequest.request_id}`,
        transformedRequest,
        { params: { bo_id: boId } },
      );
      setEditOpen(false);
      setErrors({});

      setAlertMsg("Request updated successfully!");
      setAlertSeverity("success");
      fetchData();
    } catch (err) {
      console.error("Edit failed:", err.response?.data || err.message);

      setAlertMsg("Failed to Update!");
      setAlertSeverity("error");
    }
  };

  const fetchApprovalHistory = async (requestId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/leave-request/approval-history/${requestId}`,
        { params: { bo_id: boId } },
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching approval history:", err);
      return [];
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${apiBaseUrl}/${id}`, {
        params: { bo_id: boId },
      });
      if (response.status === 200) {
        setAlertMsg("Request Deleted!");
        setAlertSeverity("success");
        await fetchData();
      }
    } catch (err) {
      console.error(
        "Delete failed, it has corresponding approval page:",
        err.response?.data || err.message,
      );
      if (err.response && err.response.data && err.response.data.error) {
        setAlertMsg(err.response.data.error); // shows "linked to projects" message
      } else {
        setAlertMsg("Something went wrong");
      }
      setAlertSeverity("error");
    }
  };

  const handleToggleFlag = async (request_id) => {
    try {
      const response = await axios.patch(
        `${apiBaseUrl}/${request_id}/toggle-flag`,
        {},
        { params: { bo_id: boId } },
      );
      if (response.status === 200) {
        setAlertMsg("Toggle flag updated successfully!");
        setAlertSeverity("success");
        await fetchData();
      }
    } catch (err) {
      console.error("Toggle failed:", err.response?.data || err.message);

      setAlertMsg("Failed to flagg toggled!");
      setAlertSeverity("error");
    }
  };

  // Cancel leave request
  const handleCancelLeave = async () => {
    if (!cancelReason.trim()) {
      setCancelReasonError("Cancellation reason is required");
      return;
    }
    if (cancelReason.trim().length > 500) {
      setCancelReasonError("Cancellation reason must be 500 characters or less");
      return;
    }
    setCancelLoading(true);
    try {
      const res = await axios.post(
        `${apiBaseUrl}/${cancelTarget.request_id}/cancel`,
        {
          employee_id: empId,
          bo_id: boId,
          cancellation_reason: cancelReason.trim(),
        }
      );
      setAlertMsg(res.data.message || "Leave request cancelled successfully");
      setAlertSeverity("success");
      setCancelDialogOpen(false);
      setCancelTarget(null);
      setCancelReason("");
      setCancelReasonError("");
      await fetchData();
    } catch (err) {
      setAlertMsg(err.response?.data?.error || "Failed to cancel leave request");
      setAlertSeverity("error");
    } finally {
      setCancelLoading(false);
    }
  };

  const renderField = (field, data, setData) => {
    // duration_type is now implicit — driven by leave_type, never rendered
    if (field === "duration_type") return null;

    const isSpecificHours = (data.leave_type || "").trim().toLowerCase() === "permission"
      || (data.duration_type || "").toLowerCase() === "specific hours";

    const requiredFields = [
      "leave_description",
      "employee_id",
      "leave_type",
      "approval_level_required",
      "start_date",
      "alternative_resource",
    ];
    if (isSpecificHours) {
      requiredFields.push("start_time", "end_time", "total_hours");
    } else {
      requiredFields.push("end_date");
    }

    const isRequired = requiredFields.includes(field);
    const labelText = `${fieldLabels[field] || field}${
      isRequired ? " " : " (Optional)"
    }`;

    // Function to calculate legend width based on actual text length
    const calculateLegendWidth = (text) => {
      // Create a temporary canvas element to measure text width
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      // Set font to match Material-UI label font (scaled down to 0.75)
      context.font = '9px "Roboto", "Helvetica", "Arial", sans-serif';

      // Measure the actual text width
      const textWidth = context.measureText(text).width;

      // Add small padding for exactly one space after label name
      return `${textWidth + 8}px`;
    };

    const commonFieldStyles = {
      width: "250px",
      mb: 1,
      mt: 1,

      "& .MuiInputLabel-root": {
        transform: "translate(14px, -9px) scale(0.75)",
        transformOrigin: "top left",
        backgroundColor: "#f9f9f9",
        padding: "0 2px",
        lineHeight: "1.2",
        whiteSpace: "nowrap",
      },

      /*  NORMAL TextField */
      "& .MuiOutlinedInput-notchedOutline legend": {
        maxWidth: calculateLegendWidth(labelText),
      },

      /*  FIX FOR DATEPICKER */
      "& .MuiOutlinedInput-notchedOutline legend span": {
        paddingRight: 0,
      },

      "& .MuiOutlinedInput-notchedOutline legend span span": {
        maxWidth: calculateLegendWidth(labelText), // ⬅ key fix
        display: "inline-block",
      },

      "& .MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
    };
    const isEmployeeDropdown = field === "employee_id";
    const isLeaveTypeDropdown = field === "leave_type";
    const isDateField = field === "start_date" || field === "end_date";
    const isEndDate = field === "end_date";
    const isTotalDays = field === "total_days";
    const isTimeField = ["start_time", "end_time"].includes(field);
    const isTotalHours = field === "total_hours";
    const isNumber = [
      "approval_level_required",
      "total_days",
      "total_hours",
    ].includes(field);

    const handleChange = (field, value) => {
      let newData = { ...data, [field]: value };
      if (isTimeField) {
        const totalHours = calculateTotalHours(
          field === "start_time" ? value : data.start_time,
          field === "end_time" ? value : data.end_time,
        );
        newData.total_hours = totalHours;
      } else if (field === "leave_type") {
        // Auto-set duration_type based on leave type
        const isPermission = (value || "").trim().toLowerCase() === "permission";
        const isHalfDay = (value || "").trim().toLowerCase() === "half day leave";
        if (isPermission) {
          newData.duration_type = "Specific Hours";
          newData.end_date = null;
          newData.total_days = null;
          newData.is_half_day = false;
        } else if (isHalfDay) {
          newData.duration_type = "Half Day";
          newData.end_date = null;
          newData.total_days = null;
          newData.is_half_day = true;
          newData.start_time = null;
          newData.end_time = null;
          newData.total_hours = null;
        } else {
          newData.duration_type = "Full/Multiple Days";
          newData.start_time = null;
          newData.end_time = null;
          newData.total_hours = null;
          newData.is_half_day = false;
        }
      } else if (isDateField) {
        if (newData.duration_type === "Full/Multiple Days") {
          const sd = field === "start_date" ? value : data.start_date;
          const ed = field === "end_date"   ? value : data.end_date;
          // Fetch holidays for the relevant years before calculating
          if (sd) fetchHolidaysForYear(new Date(sd + "T00:00:00").getFullYear());
          if (ed) fetchHolidaysForYear(new Date(ed + "T00:00:00").getFullYear());
          const totalDays = calculateWorkingDays(sd, ed);
          newData.total_days = totalDays;
        }
      }

      setData(newData);
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    if (field === "alternative_resource") {
      return (
        <TextField
          key={field}
          select
          label={`${labelText}`}
          value={data[field] || ""}
          onChange={(e) => handleChange(field, e.target.value)}
          size="small"
          sx={commonFieldStyles}
          disabled={resourcesLoading || !data.employee_id}
          error={!!errors[field]}
          helperText={errors[field]}
          required={isRequired}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">
            <em>Select alternative resource</em>
          </MenuItem>
          {resourcesLoading ? (
            <MenuItem disabled>Loading...</MenuItem>
          ) : projectResources.filter(
              (res) => res.employee_id !== data.employee_id,
            ).length === 0 ? (
            <MenuItem disabled>No teammates available</MenuItem>
          ) : (
            projectResources
              .filter((res) => res.employee_id !== data.employee_id)
              .map((res) => (
                <MenuItem key={res.employee_id} value={res.employee_id}>
                  {res.first_name} {res.last_name}
                </MenuItem>
              ))
          )}
        </TextField>
      );
    }

    if (field === "approval_level_required") {
      return (
        <TextField
          key={field}
          label={labelText}
          value={data[field] || ""}
          size="small"
          required={isRequired}
          sx={commonFieldStyles}
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    if (field === "emergency_contact") {
      return (
        <TextField
          key={field}
          label={labelText}
          value={data[field] || ""}
          onChange={(e) => handleChange(field, e.target.value)}
          size="small"
          required={isRequired}
          sx={commonFieldStyles}
          error={!!errors[field]}
          helperText={errors[field]}
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    const isHalfDayMode = (data.duration_type || "").toLowerCase() === "half day";

    const showField = () => {
      if (isEndDate || isTotalDays)
        return !isSpecificHours && !isHalfDayMode;
      if (isTimeField || isTotalHours)
        return isSpecificHours || isHalfDayMode;
      return true;
    };

    if (!showField()) return null;

    if (isEmployeeDropdown) {
      const employees = data.bo_id ? boToEmployeesMap[data.bo_id] || [] : [];
      return (
        <TextField
          key={field}
          label={labelText}
          value={
            employees.find((emp) => emp.employee_id === data[field])
              ? `${
                  employees.find((emp) => emp.employee_id === data[field])
                    .first_name
                } ${
                  employees.find((emp) => emp.employee_id === data[field])
                    .last_name
                }`
              : ""
          }
          onChange={(e) => handleChange(field, e.target.value)}
          size="small"
          required={isRequired}
          sx={commonFieldStyles}
          error={!!errors[field]}
          helperText={errors[field]}
          disabled
        ></TextField>
      );
    }

    if (isLeaveTypeDropdown) {
      return (
        <TextField
          key={field}
          select
          label={labelText}
          value={data[field] || ""}
          onChange={(e) => handleChange(field, e.target.value)}
          size="small"
          required={isRequired}
          sx={commonFieldStyles}
          error={!!errors[field]}
          helperText={errors[field]}
          disabled={leaveTypeLoading}
          InputLabelProps={{ shrink: true }}
        >
          {leaveTypeOptions.map((opt) => (
            <MenuItem
              key={opt.variable_value}
              value={opt.variable_value}
              sx={{ fontSize: "12px" }}
            >
              {opt.variable_value}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (isDateField) {
      const isStartDate = field === "start_date";
      const isEndDate = field === "end_date";

      // Determine whether this is an edit form (existing record)
      const isEditMode = !!data?.request_id; // or use your own flag like isEdit

      // Calculate min date logic
      let minDate = moment();
      let maxDate = null;
      
      if (field === "start_date") {
        // Start date: allow up to 30 days in the past, no upper limit
        minDate = moment().subtract(30, "days");
        maxDate = null; // no limit on future dates
      } else if (isEndDate) {
        // End date: must not be before start_date, but also allow up to 30 days in the past
        minDate = data.start_date
          ? moment(data.start_date)
          : moment().subtract(30, "days");
        maxDate = null;
      }

      const shouldDisable = false;
      return (
        <React.Fragment key={field}>
          <DatePicker
            label={labelText}
            value={parseISTDate(data[field])}
            onChange={(newValue) =>
              handleChange(
                field,
                newValue ? formatISTDate(newValue) : null,
              )
            }
            disabled={shouldDisable}
            minDate={minDate}
            maxDate={maxDate}
            shouldDisableDate={(date) => {
              const day = date.day(); // 0=Sun, 6=Sat
              if (day === 0 || day === 6) return true; // disable weekends
              // Block dates older than 30 days for both start and end date
              const minAllowed = moment().subtract(30, "days").startOf("day");
              if (date.isBefore(minAllowed, "day")) return true;
              // Disable full-day public holidays
              const dateStr = date.format("YYYY-MM-DD");
              const year = date.year();
              const list = holidaysCache[year] || [];
              return list.some(h => {
                if ((h.holiday_type || "Full Day") !== "Full Day") return false;
                const raw = String(h.holiday_date || "");
                const hStr = /^\d{4}-\d{2}-\d{2}$/.test(raw)
                  ? raw
                  : moment(raw).format("YYYY-MM-DD");
                return hStr === dateStr;
              });
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                required: isRequired,
                error: !!errors[field],
                helperText: shouldDisable
                  ? "Select start date first"
                  : errors[field],
                InputLabelProps: { shrink: true },
                sx: commonFieldStyles,
              },
              popper: {
                sx: {
                  "& .MuiPickersCalendar-root": {
                    width: 220,
                    fontSize: "0.8rem",
                  },
                  "& .MuiCalendarPicker-root": {
                    width: 220,
                  },
                  "& .MuiDay-root": {
                    width: 28,
                    height: 26,
                    fontSize: "0.7rem",
                  },
                },
              },
            }}
          />
          {/* Morning/Afternoon selector — only for Half Day mode */}
          {isStartDate && isHalfDayMode && (
            <TextField
              select
              label="Half Day Period *"
              value={data.half_day_period || ""}
              onChange={(e) => handleChange("half_day_period", e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              error={!!errors.half_day_period}
              helperText={errors.half_day_period || ""}
              sx={commonFieldStyles}
            >
              <MenuItem value="First Half">First Half</MenuItem>
              <MenuItem value="Second Half">Second Half</MenuItem>
            </TextField>
          )}
        </React.Fragment>
      );
    }
    // Special handling for leave_description as multiline
    if (field === "leave_description") {
      return (
        <TextField
          key={field}
          label={labelText}
          value={data[field] || ""}
          required={isRequired}
          onChange={(e) => {
            setData((prev) => ({ ...prev, [field]: e.target.value }));
            setErrors((prev) => ({ ...prev, [field]: "" }));
          }}
          size="small"
          multiline
          rows={2}
          helperText={errors[field]}
          sx={{
            width: "98%", // Full width
            mb: 1.5,
            mt: 1,
            gridColumn: "span 2", // Make it span both columns for better layout
            "& .MuiInputLabel-root": {
              transform: "translate(14px, -9px) scale(0.75)",
              transformOrigin: "top left",
              backgroundColor: "#f9f9f9",
              padding: "0 2px",
              lineHeight: "1.5",
              whiteSpace: "nowrap",
            },
            "& .MuiOutlinedInput-notchedOutline legend": {
              maxWidth: calculateLegendWidth(labelText),
              width: "auto",
            },
            "& .MuiInputBase-root": {
              padding: 0,
            },
            "& .MuiInputBase-inputMultiline": {
              padding: "8.5px 14px",
              lineHeight: "1.4375em",
            },
          }}
          InputLabelProps={{ shrink: true }}
          error={!!errors[field]}
        />
      );
    }

    return (
      <TextField
        key={field}
        type={isTimeField ? "time" : isNumber ? "number" : "text"}
        label={labelText}
        value={data[field] || ""}
        required={isRequired}
        onChange={(e) => handleChange(field, e.target.value)}
        size="small"
        sx={{
          ...commonFieldStyles,
          /*  LABEL FIX – consistent positioning for all fields (colors handled by FormThemes) */
          "& .MuiInputLabel-root": {
            transform: "translate(14px, -9px) scale(0.75)",
            transformOrigin: "top left",
            backgroundColor: "#f9f9f9",
            padding: "0 2px", // Reduced vertical padding to decrease background height
            lineHeight: "1.5", // Reduced line height for tighter spacing
            whiteSpace: "nowrap",
          },
          /*  NOTCH FIX */
          "& .MuiOutlinedInput-notchedOutline legend": {
            maxWidth: calculateLegendWidth(labelText),
            width: "auto",
          },
        }}
        InputProps={{
          readOnly: (isNumber && ["total_days", "total_hours"].includes(field)) || (isTimeField && isHalfDayMode),
          ...(isTimeField ? { inputProps: { step: 300 } } : {}),
        }}
        InputLabelProps={{ shrink: true }}
        error={!!errors[field]}
        helperText={errors[field]}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MainLayout>
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          mt={0.5}
          mb={2}
          gap={2}
        >
          <IconButton onClick={(e) => setColumnAnchorEl(e.currentTarget)}>
            <SettingsIcon fontSize="small" />
          </IconButton>
          <Popover
            open={Boolean(columnAnchorEl)}
            anchorEl={columnAnchorEl}
            onClose={() => setColumnAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 1,
                width: 200,
                maxHeight: 300,
                overflowY: "auto",
                borderRadius: 2,
              },
            }}
          >
            <Box p={1} display="flex" flexDirection="column" gap={1}>
              {leaveFields.map((field) => (
                <Box
                  key={field}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    color: "black",
                    border: "1px solid #ccc",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "#333",
                      fontWeight: "bold",
                    }}
                  >
                    {fieldLabels[field]}
                  </Typography>
                  <Switch
                    checked={columnVisibility[field]}
                    onChange={() =>
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [field]: !prev[field],
                      }))
                    }
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2DB0E4",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#2DB0E4",
                        },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Popover>

          <Button
            variant="contained"
            color="primary"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              px: 2.5,
              height: 36,
              backgroundColor: "#2DB0E4",
            }}
            startIcon={<Add />}
            onClick={() => {
              setNewRequest({
                duration_type: "Full/Multiple Days",
                bo_id: boId,
                employee_id: empId || "",
                leave_type: "",
                leave_description: "",
                approval_level_required: approvalLevel || null,
                start_date: null,
                end_date: null,
                total_days: null,
                start_time: null,
                end_time: null,
                total_hours: null,
                is_half_day: false,
                status: "Pending",
                emergency_contact: null,
                is_draft: false,
                alternative_resource: null,
              });
              setOpen(true);
            }}
          >
            Add
          </Button>
          <TextField
            size="small"
            sx={{
              width: 250,
              "& input": { fontSize: 14 },
              "& input::placeholder": { fontSize: 12 },
            }}
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              sx: {
                height: 36,
                paddingRight: 0,
              },
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{
              width: 200,
              // Make height match the 36px of the search field
              "& .MuiInputBase-root": {
                height: 36,
              },

              // Fix vertical alignment of text
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                fontSize: 12,
                padding: "0 14px",
              },

              "& .MuiInputLabel-root": { fontSize: "14px" },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "14px",
                      minHeight: "32px",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            {statusOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box
          sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}
        >
          {alertMsg && (
            <Box mb={2}>
              <Alert
                onClose={() => setAlertMsg("")}
                variant="standard"
                sx={{
                  color: alertSeverity === "success" ? "#2E9F50" : "#FF0C0C",
                  fontWeight: 500,
                  backgroundColor: "#fff",
                }}
              >
                {alertMsg}
              </Alert>
            </Box>
          )}
          <ThemeProvider theme={Table_theme}>
            <CssBaseline />
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      Actions
                    </TableCell>
                    {leaveFields
                      .filter((f) => columnVisibility[f])
                      .map((field) => (
                        <TableCell
                          key={field}
                          sx={{
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ gap: 0.5 }}
                          >
                            <strong>{fieldLabels[field]}</strong>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterIconClick(field, e)}
                            >
                              <FilterList fontSize="small" />
                            </IconButton>
                            <Popover
                              open={Boolean(anchorEls[field])}
                              anchorEl={anchorEls[field]}
                              onClose={() => handleFilterClose(field)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <Box
                                p={2}
                                display="flex"
                                flexDirection="column"
                                gap={1}
                                width={200}
                              >
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder={`Filter ${fieldLabels[field]}`}
                                  value={filters[field] || ""}
                                  onChange={(e) =>
                                    setFilters((prev) => ({
                                      ...prev,
                                      [field]: e.target.value,
                                    }))
                                  }
                                />
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                >
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleFilterClose(field)}
                                    sx={{ minWidth: 70 }}
                                  >
                                    Apply
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                      setFilters((prev) => ({
                                        ...prev,
                                        [field]: "",
                                      }));
                                      handleFilterClose(field);
                                    }}
                                    sx={{ minWidth: 70 }}
                                  >
                                    Clear
                                  </Button>
                                </Box>
                              </Box>
                            </Popover>
                          </Box>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          leaveFields.filter((f) => columnVisibility[f])
                            .length + 1
                        }
                        align="center"
                        sx={{
                          py: 4,
                          color: "text.secondary",
                          fontSize: "13px",
                        }}
                      >
                        No results found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((row, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          "& td": {
                            fontSize: "12px",
                            paddingTop: "40px",
                            paddingBottom: "40px",
                          },
                        }}
                      >
                        <TableCell>
                          {(() => {
                            const rowStatus = (
                                row.approval_status ||
                                row.status ||
                                ""
                              ).toLowerCase();
                            const isLocked = rowStatus === "approved"
                              || rowStatus === "cancelled"
                              || rowStatus === "cancellation rejected";
                            const isCancellationPending = rowStatus.startsWith("cancellation pending") || (rowStatus.startsWith("waiting for") && rowStatus.includes("cancellation"));
                            const isCancelled = rowStatus === "cancelled";
                            const isRejected = rowStatus === "rejected";
                            return (
                              <Box
                                display="flex"
                                gap={0.5}
                                justifyContent="center"
                              >
                                <Tooltip
                                  title={
                                    isLocked || isCancellationPending
                                      ? "Cannot edit approved request"
                                      : "Edit"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={isLocked || isCancellationPending}
                                      onClick={() => {
                                        setEditRequest({
                                          ...row,
                                          start_date: row.start_date 
                                            ? String(row.start_date).slice(0, 10)
                                            : null,
                                          end_date: row.end_date
                                            ? String(row.end_date).slice(0, 10)
                                            : null,
                                          total_days: row.total_days
                                            ? Math.floor(row.total_days)
                                            : null,
                                          start_time: row.start_time
                                            ? row.start_time.slice(0, 5)
                                            : null,
                                          end_time: row.end_time
                                            ? row.end_time.slice(0, 5)
                                            : null,
                                          total_hours: row.total_hours || null,
                                          status: row.status || null,
                                          emergency_contact:
                                            row.emergency_contact || null,
                                          alternative_resource:
                                            row.alternative_resource || null,
                                          is_draft: row.is_draft || false,
                                          is_half_day: row.is_half_day || false,
                                          approval_level_required:
                                            row.approval_level_required || null,
                                        });
                                        setEditOpen(true);
                                      }}
                                      sx={{
                                        color: isLocked || isCancellationPending ? "#bdbdbd" : "#2DB0E4",
                                      }}
                                    >
                                      <EditOutlinedIcon
                                        sx={{ fontSize: "16px" }}
                                      />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                {/* <Tooltip
                                  title={
                                    isLocked
                                      ? "Cannot delete approved request"
                                      : "Delete"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={isLocked}
                                      onClick={() => confirmDelete(row)}
                                    >
                                      <DeleteIcon
                                        fontSize="small"
                                        sx={{
                                          color: isLocked ? "#bdbdbd" : "red",
                                        }}
                                      />
                                    </IconButton>
                                  </span>
                                </Tooltip> */}
                                {/* <Tooltip title={isLocked? "cannot update approved request": "Toggle Delete Flag"}>
                                  <span>
                                  <IconButton
                                    size="small"
                                    disabled = {isLocked}
                                    onClick={() =>
                                      handleToggleFlag(row.request_id)
                                    }
                                  >
                                    <FlagIcon
                                      style={{
                                        color: 
                                          row.delete_flag === "N"
                                            ? "#2E9F50"
                                            : "#FF0C0C",
                                        transition: "color 0.2s",
                                        width: 17,
                                        height: 17,
                                        verticalAlign: "middle",
                                      }}
                                    />
                                  </IconButton>
                                  </span>
                                </Tooltip> */}
                                {/* Cancel Leave Icon */}
                                <Tooltip title={
                                  isCancelled ? "Leave already cancelled"
                                  : isRejected ? "Leave was rejected"
                                  : isCancellationPending ? "Cancellation already pending"
                                  : "Cancel Leave"
                                }>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={isCancelled || isRejected || isCancellationPending}
                                      onClick={() => {
                                        if (!isCancelled && !isRejected && !isCancellationPending) {
                                          setCancelTarget(row);
                                          setCancelReason("");
                                          setCancelReasonError("");
                                          setCancelDialogOpen(true);
                                        }
                                      }}
                                      sx={{ color: (isCancelled || isRejected || isCancellationPending) ? "#ccc" : "#d32f2f" }}
                                    >
                                      <CancelOutlinedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            );
                          })()}
                        </TableCell>

                        {leaveFields
                          .filter((f) => columnVisibility[f])
                          .map((field) => (
                            <TableCell
                              key={field}
                              sx={{
                                fontSize: "0.7rem",
                                whiteSpace:
                                  field === "leave_description"
                                    ? "normal"
                                    : "nowrap",
                                verticalAlign: "middle",
                                padding: "16px !important",
                              }}
                            >
                              {field === "leave_description" ? (
                                <Box
                                  sx={{
                                    maxWidth: "350px",
                                    minWidth: "250px",
                                    maxHeight: "60px",
                                    overflow: "auto",
                                    wordBreak: "break-word",
                                    lineHeight: "1.4",
                                  }}
                                >
                                  {row[field]}
                                </Box>
                              ) : [
                                  "start_date",
                                  "end_date",
                                  "submitted_at",
                                  "last_updated",
                                ].includes(field) && row[field] ? (
                                ["start_date", "end_date"].includes(field)
                                  ? (row[field] ? moment(String(row[field]).slice(0, 10), "YYYY-MM-DD").format("MM/DD/YYYY") : "")
                                  : moment(row[field]).format("MM/DD/YYYY")
                              ) : field === "leave_type" ? (
                                row[field]
                              ) : field === "employee_name" ? (
                                row.bo_id && boToEmployeesMap[row.bo_id] ? (
                                  boToEmployeesMap[row.bo_id].find(
                                    (emp) =>
                                      emp.employee_id === row.employee_id,
                                  ) ? (
                                    `${
                                      boToEmployeesMap[row.bo_id].find(
                                        (emp) =>
                                          emp.employee_id === row.employee_id,
                                      ).first_name
                                    } ${
                                      boToEmployeesMap[row.bo_id].find(
                                        (emp) =>
                                          emp.employee_id === row.employee_id,
                                      ).last_name
                                    }`
                                  ) : (
                                    row.employee_id
                                  )
                                ) : (
                                  row.employee_id
                                )
                              ) : field === "alternative_resource" ? (
                                getAlternativeResourceName(
                                  row.alternative_resource_name,
                                )
                              ) : field === "approver_id" ? (
                                row.approvalHistory &&
                                row.approvalHistory.length > 0 ? (
                                  row.approvalHistory.map((a) => (
                                    <span
                                      key={a.approver_id}
                                      title={`ID: ${a.approver_id}`}
                                    >
                                      {a.approver_name}
                                    </span>
                                  ))
                                ) : (
                                  " "
                                )
                              ) : field === "total_days" ? (
                                formatDays(row[field])
                              ) : field === "total_hours" ? (
                                formatHours(row[field])
                              ) : field === "status" ? (
                                (() => {
                                  const s = (row[field] || "")
                                    .toLowerCase()
                                    .trim();
                                  let label = row[field] || "Pending";
                                  let bg = "#fff8e1",
                                    color = "#e65100";
                                  if (s === "approved") {
                                    label = "Approved";
                                    bg = "#e8f5e9";
                                    color = "#2e7d32";
                                  } else if (s === "rejected") {
                                    label = "Rejected";
                                    bg = "#ffebee";
                                    color = "#c62828";
                                  } else if (s === "cancelled") {
                                    label = "Cancelled";
                                    bg = "#f5f5f5";
                                    color = "#616161";
                                  } else if (s === "cancellation rejected") {
                                    label = "Cancellation Rejected";
                                    bg = "#ffebee";
                                    color = "#c62828";
                                  } else if (s.startsWith("cancellation pending") || (s.startsWith("waiting for") && s.includes("cancellation"))) {
                                    label = "Cancellation Pending";
                                    bg = "#fff3e0";
                                    color = "#e65100";
                                  } else if (/^pending,\s*1\s*of\s*\d+\s*approved$/.test(s)) {
                                    label = row[field];
                                    bg = "#e3f2fd";
                                    color = "#1565c0";
                                  } else if (/^pending,\s*2\s*of\s*\d+\s*approved$/.test(s)) {
                                    label = row[field];
                                    bg = "#e3f2fd";
                                    color = "#1565c0";
                                  } else if (s.startsWith("pending, ")) {
                                    label = row[field];
                                    bg = "#e3f2fd";
                                    color = "#1565c0";
                                  } else if (
                                    s === "waiting for admin approval"
                                  ) {
                                    label = "Awaiting Admin";
                                    bg = "#f3e5f5";
                                    color = "#6a1b9a";
                                  } else if (
                                    s === "waiting for system admin approval"
                                  ) {
                                    label = "Awaiting System Admin";
                                    bg = "#f3e5f5";
                                    color = "#6a1b9a";
                                  } else if (s === "waiting for pm approval") {
                                    label = "Waiting for PM Approval";
                                    bg = "#e3f2fd";
                                    color = "#1565c0";
                                  } else if (s === "waiting for hr approval") {
                                    label = "Waiting for HR Approval";
                                    bg = "#e8eaf6";
                                    color = "#283593";
                                  }
                                  return (
                                    <Box
                                      component="span"
                                      sx={{
                                        display: "inline-block",
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        bgcolor: bg,
                                        color,
                                      }}
                                    >
                                      {label}
                                    </Box>
                                  );
                                })()
                              ) : (
                                row[field]
                              )}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(totalCount / rowsPerPage)}
                page={page + 1}
                onChange={(_, newPage) => setPage(newPage - 1)}
                color="primary"
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "8px",
                    minWidth: "32px",
                    height: "32px",
                    fontSize: "14px",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#b3dbea !important",
                    color: "black",
                    fontWeight: "bold",
                  },
                  "& .MuiPaginationItem-ellipsis": {
                    display: "none",
                  },
                }}
              />
            </Box>
          </ThemeProvider>
        </Box>

        <ThemeProvider theme={FormThemes}>
          <CssBaseline />
          <Dialog
            open={open}
            onClose={(_, reason) => {
              if (reason === "backdropClick") return;
              setOpen(false);
              setErrors({});
              setDialogError("");
            }}
            fullWidth
            maxWidth="sm"
            sx={{ top: "-20%" }}
          >
            <DialogTitle
              sx={{
                borderRadius: "12px",
                bgcolor: "#f5f5f5",
                p: 1,
                fontSize: "16px",
                fontWeight: 600,
                borderBottom: "1px solid #ddd",
              }}
            >
              Add Leave Request
              <IconButton
                onClick={() => { setOpen(false); setErrors({}); setDialogError(""); }}
                sx={{ position: "absolute", right: 8, top: 5 }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "0.5px",
                columnGap: 0,
                paddingTop: 2,
                p: "16px 40px 2px 40px",
                mt: 1,
              }}
            >
              {dialogError && (
                <Alert severity="error" sx={{ gridColumn: "span 2", mb: 1 }} onClose={() => setDialogError("")}>
                  {dialogError}
                </Alert>
              )}
              {popupFields.map((field) =>
                renderField(field, newRequest, setNewRequest),
              )}
            </DialogContent>
            <DialogActions sx={{ px: "48px", py: 1.5, gap: 1 }}>
              <Button
                onClick={() => {
                  setOpen(false);
                  setErrors({});
                  setDialogError("");
                }}
                variant="outlined"
                size="small"
                sx={{ height: 40, minWidth: 80 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                variant="contained"
                size="small"
                disabled={isSaving}
                sx={{ height: 40, minWidth: 80 }}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={editOpen}
            onClose={(_, reason) => {
              if (reason === "backdropClick") return;
              setEditOpen(false);
              setErrors({});
            }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle
              sx={{
                borderRadius: "12px",
                bgcolor: "#f5f5f5",
                p: 1,
                fontSize: "16px",
                fontWeight: 600,
                borderBottom: "1px solid #ddd",
              }}
            >
              Update Leave Request
              <IconButton
                onClick={() => { setEditOpen(false); setErrors({}); }}
                sx={{ position: "absolute", right: 8, top: 5 }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "0.5px",
                columnGap: 0,
                paddingTop: 2,
                p: "16px 40px 2px 40px",
                mt: 1,
              }}
            >
              {popupFields.map((field) =>
                renderField(field, editRequest, setEditRequest),
              )}
            </DialogContent>
            <DialogActions sx={{ px: "40px", py: 1.5, gap: 1 }}>
              <Button
                onClick={() => {
                  setEditOpen(false);
                  setErrors({});
                }}
                variant="outlined"
                size="small"
                sx={{ height: 40, minWidth: 80 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  fontWeight: "bold",
                  borderRadius: "8px",
                  minWidth: 80,
                  height: 40,
                  backgroundColor: "#45a049",
                }}
                onClick={handleEdit}
              >
                Update
              </Button>
            </DialogActions>
          </Dialog>
          {getConfirmDeleteDialog({
            open: deleteDialogOpen,
            onClose: () => {
              setDeleteDialogOpen(false);
              setSelectedRequest(null);
            },
            onConfirm: () => {
              if (selectedRequest) {
                handleDelete(selectedRequest.request_id);
              }
              setDeleteDialogOpen(false);
              setSelectedRequest(null);
            },
            title: "Confirm Deletion",
            contentText: selectedRequest
              ? `Are you sure you want to delete the request for ${getEmployeeName(
                  selectedRequest,
                )}?`
              : "Are you sure you want to delete this request?",
          })}

          {/* ── Cancel Leave Dialog ── */}
          <Dialog
            open={cancelDialogOpen}
            onClose={() => { if (!cancelLoading) { setCancelDialogOpen(false); setCancelTarget(null); setCancelReason(""); setCancelReasonError(""); } }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              Cancel Leave Request
              <IconButton size="small" onClick={() => { if (!cancelLoading) { setCancelDialogOpen(false); setCancelTarget(null); setCancelReason(""); setCancelReasonError(""); } }}>
                ✕
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pb: 0 }}>
              {cancelTarget && (() => {
                const s = (cancelTarget.status || "").toLowerCase();
                const isApproved = s === "approved";
                const today = new Date(); today.setHours(0,0,0,0);
                const start = new Date(cancelTarget.start_date); start.setHours(0,0,0,0);
                const end = new Date(cancelTarget.end_date || cancelTarget.start_date); end.setHours(0,0,0,0);
                const isSameDay = isApproved && today >= start && today <= end;
                return (
                  <Box>
                    {isSameDay && (
                      <Alert severity="warning" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
                        This leave is active today. Cancellation will require approval from your manager hierarchy.
                      </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Please provide a reason for cancelling this leave request.
                    </Typography>
                    <TextField
                      label="Cancellation Reason"
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      value={cancelReason}
                      onChange={(e) => { setCancelReason(e.target.value); if (cancelReasonError) setCancelReasonError(""); }}
                      error={!!cancelReasonError}
                      helperText={cancelReasonError || `${cancelReason.length}/500`}
                      inputProps={{ maxLength: 500 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                );
              })()}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{ height: 40, minWidth: 80 }}
                onClick={() => { setCancelDialogOpen(false); setCancelTarget(null); setCancelReason(""); setCancelReasonError(""); }}
                disabled={cancelLoading}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                sx={{ height: 40, minWidth: 120 }}
                onClick={handleCancelLeave}
                disabled={cancelLoading}
              >
                {cancelLoading ? "Cancelling..." : "Confirm Cancel"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Long Leave Warning Dialog */}
          <Dialog open={longLeaveWarning} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 600, fontSize: "15px", bgcolor: "#fff8e1", borderBottom: "1px solid #ffe082" }}>
              ⚠️ Long Leave Warning
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Typography sx={{ fontSize: "14px" }}>
                You are requesting <strong>{pendingRequest?.total_days} days</strong> of leave. This is a long holiday. Are you sure you want to proceed?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{ height: 36, minWidth: 80 }}
                onClick={() => { setLongLeaveWarning(false); setPendingRequest(null); }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                color="warning"
                sx={{ height: 36, minWidth: 80 }}
                onClick={async () => {
                  setLongLeaveWarning(false);
                  await submitLeaveRequest(pendingRequest);
                  setPendingRequest(null);
                }}
              >
                Yes, Proceed
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </MainLayout>
    </LocalizationProvider>
  );
};

export default LeaveRequest;